import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { Plus, Receipt, X, Trash2 } from 'lucide-react';
import AmountInput from '../components/AmountInput';

const Expenses = () => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ category: '', amount: '', notes: '' });

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try { const res = await api.get('/expenses'); setExpenses(res.data); } catch (err) {}
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses', { ...form, amount: Number(form.amount) });
      setShowModal(false);
      setForm({ category: '', amount: '', notes: '' });
      fetchExpenses();
    } catch (err) { alert('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/expenses/${id}`); fetchExpenses(); } catch (err) {}
  };

  const formatCurrency = (v) => 'TZS ' + Number(v || 0).toLocaleString('en');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h2 className="text-xl md:text-2xl font-bold text-white">{t('expenses')}</h2><p className="text-gray-400 text-sm mt-1">Track your business costs</p></div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-orange-600 transition text-sm shadow-lg shadow-orange-500/20"><Plus size={18} /> Add Expense</button>
      </div>
      {expenses.length === 0 ? (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-12 text-center"><Receipt size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-400 font-medium">{t('no_data')}</p></div>
      ) : (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/5"><th className="text-left p-4 text-gray-400 text-xs font-medium">Date</th><th className="text-left p-4 text-gray-400 text-xs font-medium">Category</th><th className="text-left p-4 text-gray-400 text-xs font-medium">Notes</th><th className="text-right p-4 text-gray-400 text-xs font-medium">Amount</th><th className="p-4"></th></tr></thead>
              <tbody>
                {expenses.map((exp) => (
                  <tr key={exp.id} className="border-b border-white/5 hover:bg-white/[0.01]">
                    <td className="p-4 text-white text-sm">{exp.expenseDate}</td><td className="p-4 text-gray-300 text-sm">{exp.category}</td><td className="p-4 text-gray-500 text-sm">{exp.notes || '-'}</td>
                    <td className="p-4 text-red-400 text-sm font-medium text-right">{formatCurrency(exp.amount)}</td>
                    <td className="p-4"><button onClick={() => handleDelete(exp.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111a14] border border-white/10 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6"><h3 className="text-white font-bold text-lg">Add Expense</h3><button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button></div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm text-gray-300 mb-2">Category</label><input type="text" value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="e.g. Rent, Transport" className="w-full px-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500/50" required /></div>
              <div><label className="block text-sm text-gray-300 mb-2">Amount (TZS)</label><AmountInput value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0" className="w-full px-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500/50" required /></div>
              <div><label className="block text-sm text-gray-300 mb-2">Notes</label><input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional" className="w-full px-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500/50" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 border border-white/10 rounded-xl text-gray-400 hover:text-white transition text-sm">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition text-sm">Save Expense</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
